import type { OpLevel } from "@/config/operatingPoints";

/** One finding the released triage device flagged at its notification operating point. */
export interface TriageFlag {
  condition: string;
  display_name: string;
  /** Null for flat (present/absent) releases, which decide at their own operating point. */
  operating_point: OpLevel | null;
  top_slices: string[];
}

/** Reg-owned wording a device's notification carries wherever it is shown
 * (web-demo-backend src/config/triage_labeling/<device>.json). */
export interface TriageLabeling {
  key_image_caption: string;
  clinician_note: string;
}

/** Backend `triage_review` projection (web-demo-backend src/triage_review.py). */
export interface TriageReview {
  device: string;
  version: string;
  labeling: TriageLabeling;
  successful_inference: boolean;
  /** The device's own status message (e.g. "No valid series found for inference"). */
  message?: string | null;
  /** `[]` = no flag (never a negative call); `null` only when inference failed. */
  flagged: TriageFlag[] | null;
}

/** Worklist presentation derived from the emitted flags, never detected_at_* lists. */
export interface TriageWorklistSummary {
  label: string;
  findings: string[];
  count: number | null;
  flagged: boolean;
}

export function summarizeTriageResult(result: TriageReview | undefined): TriageWorklistSummary {
  if (!result) return { label: "Results unavailable", findings: [], count: null, flagged: false };
  if (!result.successful_inference) {
    return { label: "Inference unsuccessful", findings: [], count: null, flagged: false };
  }
  if (!result.flagged) {
    return { label: "Results unavailable", findings: [], count: null, flagged: false };
  }
  if (result.flagged.length === 0)
    return { label: "No flag", findings: [], count: 0, flagged: false };
  const findings = result.flagged.map((flag) => flag.display_name);
  return { label: "Flagged", findings, count: findings.length, flagged: true };
}
